import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { fetchSOWMessages, createSOWMessage } from '../services/api';
import { isValueEmpty } from '../services/utils';
import { CONSTANTS } from '../constants/constants';
import { createTicketScopedPersistence } from './ticketScopedPersistence';

// Follows the ticket in the URL, including in-app switches that never reload the page.
// Only the message data is persisted: the polling handle must survive a ticket switch.
const ticketPersistence = createTicketScopedPersistence({
  prefix: 'sow-messages-storage-',
  persistedFields: ['sowMessages', 'messagesLastUpdated', 'hasNewMessages'],
  resetState: {
    sowMessages: null, sowMessagesError: null, sowMessagesLoading: false,
    messagesLastUpdated: null, hasNewMessages: false
  }
});

export const useSOWMessagesStore = create(
  persist(
    (set, get) => ({
      sowMessages: null,
      sowMessagesLoading: false,
      sowMessagesError: null,
      messagesLastUpdated: null,
      hasNewMessages: false,
      pollingInterval: null,
      isPolling: false,
      activeTab: null,
      
      setActiveTab: (tab) => {
        set({ activeTab: tab });
      },
      // Fetch data from backend
      fetchSOWMessages: async (messagesViewed = false) => {
        const pendingSwitch = ticketPersistence.followTicket(useSOWMessagesStore);
        if (pendingSwitch) await pendingSwitch;
        set({ sowMessagesLoading: true, sowMessagesError: null });
        const isStillCurrentTicket = ticketPersistence.trackTicket();

        try {
          const data = await fetchSOWMessages();
          if (!isStillCurrentTicket()) {
            // The technician switched tickets while this loaded; it is not theirs to show or save.
            set({ sowMessagesLoading: false });
            return data;
          }
          if (data === undefined) {
            // Skipped because an identical request is already running; keep what we have.
            set({ sowMessagesLoading: false });
            return get().sowMessages;
          }
          let hasNewMessages = get().hasNewMessages;
          if (!isValueEmpty(data)) {
            // Check if there are new messages
            for (let i=0;i< data.length; i++) {
                if (!data[i].viewed) {
                    hasNewMessages = true;
                    break;
                }
            }
          }

          hasNewMessages = get().activeTab === CONSTANTS.TABS.MESSAGES ? false : hasNewMessages;

          set({ 
            sowMessages: data,
            sowMessagesLoading: false,
            sowMessagesError: null,
            messagesLastUpdated: new Date().toISOString(),
            hasNewMessages: hasNewMessages
          });
          return data;
        } catch (error) {
          set(isStillCurrentTicket()
            ? { sowMessagesError: error.message, sowMessagesLoading: false }
            : { sowMessagesLoading: false });
          throw error;
        }
      },
      
      // Create a new message
      createSOWMessage: async (messageData, t) => {
          const pendingSwitch = ticketPersistence.followTicket(useSOWMessagesStore);
          if (pendingSwitch) await pendingSwitch;
          set({ sowMessagesLoading: true, sowMessagesError: null });
          const isStillCurrentTicket = ticketPersistence.trackTicket();

          try {
            let newMessage = await createSOWMessage(messageData);
            if (!isStillCurrentTicket()) {
              // Sent for the previous ticket: it must not be appended to the new one's messages.
              set({ sowMessagesLoading: false });
              return newMessage;
            }
            if (!isValueEmpty(newMessage)) {
                if (newMessage.queued) {
                    newMessage = {
                      "id": t('na'),
                      "author": t('me'),
                      "body": messageData,
                      "created": Date.now(),
                      "updated": Date.now() 
                    }; 
                }
                const currentMessages = get().sowMessages;
                            
                // Update the messages array with the new message
                const updatedMessages = currentMessages 
                  ? [...currentMessages, newMessage]
                  : [newMessage];
                  
                set({ 
                  sowMessages: updatedMessages,
                  sowMessagesLoading: false,
                  sowMessagesError: null,
                  messagesLastUpdated: new Date().toISOString()
                });
                
                return newMessage;
            }
            // Nothing came back (failed or skipped send): don't leave the tab on its spinner.
            set({ sowMessagesLoading: false });
            
          } catch (error) {
            set(isStillCurrentTicket()
              ? { sowMessagesError: error.message, sowMessagesLoading: false }
              : { sowMessagesLoading: false });
            throw error;
          }
      },

      // Start polling for new messages
      startPolling: (interval = 5 * 60 * 1000) => { // Default 5 minutes
        const { stopPolling, fetchSOWMessages } = get();

        // Clear any existing polling
        stopPolling();

        const poll = setInterval(async () => {
          try {
            await fetchSOWMessages();
          } catch (error) {
            console.error('Polling error:', error);
            // Don't stop polling on error, just log it
          }
        }, interval);

        set({ 
          pollingInterval: poll,
          isPolling: true
        });
        
        // Initial fetch
        fetchSOWMessages();
      },

      // Stop polling
      stopPolling: () => {
        const { pollingInterval } = get();
        if (pollingInterval) {
          clearInterval(pollingInterval);
        }
        set({ 
          pollingInterval: null,
          isPolling: false
        });
      },

      // Reset new messages flag
      clearNewMessagesFlag: () => {
        set({ hasNewMessages: false });
      },

      // Toggle polling on/off
      togglePolling: (interval = 5 * 60 * 1000) => {
        const { isPolling, startPolling, stopPolling } = get();
        if (isPolling) {
          stopPolling();
        } else {
          startPolling(interval);
        }
      },

      // Get current SOW messages
      getSOWMessages: () => {
        return get().sowMessages;
      },

      // Replace entire SOW messages object
      setSOWMessages: (newData) => {
        set({ 
          sowMessages: newData,
          messagesLastUpdated: new Date().toISOString()
        });
      },

      // Clear all SOW data
      clearSOWMessages: () => {
        const { stopPolling } = get();
        stopPolling();
        set({ 
          sowMessages: null,
          sowMessagesError: null,
          messagesLastUpdated: null,
          hasNewMessages: false
        });
      }
    }),
    // Partitioned per ticket — see ticketScopedPersistence.
    ticketPersistence.persistOptions
  )
);